// @flow
import React from 'react';
import gql from 'graphql-tag';
import classNames from 'classnames';

import './GeoDistance.less';

export const fragments = {
    SERPHit: gql`
        fragment GeoDistanceSERPHit on SERPHit {
            geoDistance {
                default {
                    text
                }
            }
        }
    `
};

export type GeoDistanceSchema = {
    default: ?{
        text: string
    }
};

// GraphQL can return a null geoDistance field
export type Shape = {
    geoDistance: GeoDistanceSchema | null
};

// But geoDistance is considered required for this component!
export type Props = {
    brand: string,
    geoDistance: GeoDistanceSchema,
    isHighlighted: boolean
};

const defaultProps = {
    isHighlighted: false
};

const getBrandFill = (brand) =>
    brand === 'vrbo' ? 'fill-vrbo-secondary' : 'fill-homeaway-secondary';

const getBrandStroke = (brand) =>
    brand === 'vrbo' ? 'stroke-vrbo-secondary' : 'stroke-homeaway-secondary';

export default function GeoDistance({
    brand,
    geoDistance: {default: defaultRelation},
    isHighlighted
}: Props) {
    const geoDistanceClass = classNames('GeoDistance', {
        'GeoDistance--isHighlighted': isHighlighted
    });

    const {text} = defaultRelation || {};

    if (!text) {
        return null;
    }

    const normalizedBrand = brand.toLowerCase();

    // TODO: get baseline to provide granular fill-secondary and stroke-secondary
    // classes and use those instead!
    const brandFill = getBrandFill(normalizedBrand);
    const brandStroke = getBrandStroke(normalizedBrand);

    /**
     * Visual explanation:
     * - default state is outlined pin shape stroked with current text color,
     *   with solid dot in the middle.
     * - highlighted state is fully opaque pin shape, filled w/ brand secondary
     *   color (highlighted map pin color)
     */
    const iconPinPathClass = classNames(
        'GeoDistance__iconPinPath',
        !isHighlighted ? 'fill-transparent' : brandFill,
        !isHighlighted ? 'stroke-currentColor' : brandStroke
    );

    const iconPinHoleClass = classNames(
        'GeoDistance__iconPinHole',
        'stroke-none',
        !isHighlighted ? 'fill-currentColor' : brandFill
    );

    return (
        <div className={geoDistanceClass}>
            <svg
                aria-hidden="true"
                className="GeoDistance__icon"
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="14"
                viewBox="0 0 10 14"
            >
                <path
                    className={iconPinPathClass}
                    d="M4.832 13.342l.005.007c.12.173.207.173.33-.007a48.424 48.424 0 0 0 .682-.99 49.957 49.957 0 0 0 1.507-2.381 33.65 33.65 0 0 0 .88-1.577C9.047 6.84 9.5 5.591 9.5 4.82 9.5 2.438 7.49.5 5 .5S.5 2.438.5 4.82c0 .771.454 2.02 1.263 3.574.263.504.558 1.032.88 1.577a49.957 49.957 0 0 0 2.19 3.371zm-.814.58l-.002-.003.005.007-.003-.003z"
                />
                <ellipse
                    className={iconPinHoleClass}
                    cx="5"
                    cy="5.212"
                    rx="1.25"
                    ry="1.212"
                />
            </svg>
            <span className="GeoDistance__text">{text}</span>
        </div>
    );
}

GeoDistance.defaultProps = defaultProps;
